import { useEffect, useMemo, useState } from "react";
import { useProductOrderCalculationQuery } from "./useProductOrderCalculationQuery";
import { OrderInterface } from "../../../interfaces/orderInterface";
import { useProductsOrderStore } from "../../../stores/useProductsOrderStore";
import { FormikProps } from "formik";
import { useDebounce } from "../../../utils/useDebounce";

type UseProductOrderCalculationParams = {
  formik: FormikProps<OrderInterface>;
};

export const useOrderCalculation = (
  params: UseProductOrderCalculationParams,
) => {
  const { formik } = params;
  const {
    dealerId,
    applyPromotion,
    specialDiscount,
    shippingCost,
    applyShippingCost,
    _id: orderId,
  } = formik.values;
  // We use the zustand store to manage selected products across the order form
  const selectedProducts = useProductsOrderStore((s) => s.selectedProducts);
  const allowCalculate = useProductsOrderStore((s) => s.allowCalculate);
  const clearProducts = useProductsOrderStore((s) => s.clearProducts);

  const calculationParams = useMemo(() => {
    return {
      dealerId,
      products: selectedProducts ?? [],
      applyPromotion: applyPromotion ?? true,
      specialDiscount: specialDiscount ?? 0,
      shippingCost,
      applyShippingCost,
      orderId,
    };
  }, [
    dealerId,
    selectedProducts,
    applyPromotion,
    specialDiscount,
    shippingCost,
    applyShippingCost,
    orderId,
  ]);

  const debouncedCalculationParams = useDebounce(calculationParams, 500);
  const { data, isLoading: isCalculating } = useProductOrderCalculationQuery({
    ...debouncedCalculationParams,
    enabled: !!debouncedCalculationParams.dealerId && allowCalculate,
  });

  useEffect(() => {
    const handleOrderCalculation = () => {
      if (!isCalculating && data) {
        const { productOrderList, subTotal, iva, isr, total } = data;
        formik.setValues(
          {
            ...formik.values,
            products: productOrderList,
            subTotal,
            iva,
            isr,
            total,
          },
          false,
        );
        return;
      }
    };
    handleOrderCalculation();
  }, [dealerId, isCalculating, data, selectedProducts]);

  const handleClearOrderCalculation = () => {
    clearProducts();
    formik.setFieldValue("products", []);
  };

  return {
    isCalculating,
    handleClearOrderCalculation,
  };
};
